import React from "react";
import styled from "styled-components";
import { useRouter } from "next/router";

import { FlexContainer, FlexDiv } from "@/components/Atoms/Box";
import SVGSprite from "@/components/Atoms/SVGSprite";
import PartnerForm from "@/components/Organisms/PartnerForm";

import { generateUrlArguments } from "@/utils";
import { Typography } from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";
import { DEFAULT_URL_ARGUMENTS } from "@/constants";

function HowItWorks({
  healthDetailsData = [],
  isInternationalPayment,
  callBackRef,
  isWebinarPage,
}) {
  let urlParams = "";
  if (typeof window !== "undefined") {
    urlParams = window.location.search || DEFAULT_URL_ARGUMENTS;
  }

  const router = useRouter();
  const { slug } = router?.query || {};
  const showCallBackCTA = [
    "3-week-wellness-bootcamp",
    "4-week-nutrition-gut-health-program",
  ].includes(slug);

  const urlsArguments = generateUrlArguments(urlParams);

  return (
    <Wrapper>
      <Group>
        <LCol
          flexDirection={"column"}
          reverseOrderForMobile={[
            "3-week-wellness-bootcamp",
            "4-week-nutrition-gut-health-program",
          ].includes(slug)}
        >
          <Typography
            variant="title1"
            mb={"26px"}
            color={theme.colors.gray_90}
            textTransform={"uppercase"}
          >
            {healthDetailsData?.how_it_works?.title}
          </Typography>
          <Typography
            variant="header3"
            mb={"26px"}
            color={theme.colors.link2Color}
            fontFamily={"var(--font-600)"}
          >
            {healthDetailsData?.how_it_works?.subtitle}
          </Typography>
          {healthDetailsData?.how_it_works?.listing.length > 0 && (
            <UList>
              {healthDetailsData?.how_it_works?.listing?.map((item, index) => {
                return (
                  <ListItem key={index}>
                    <Icon
                      icon="icon-how-it-work-check"
                      fill={theme.colors.textGradient.type3static}
                    />
                    <Typography
                      variant="paragraph1"
                      color={theme.colors.gray_90}
                    >
                      {item.title}
                    </Typography>
                  </ListItem>
                );
              })}
            </UList>
          )}
        </LCol>
        <Col>
          <CallBackFormBox width={"100%"} ref={callBackRef}>
            <PartnerForm
              isHealthPage
              showAppDownLoadButtonLink
              formTitle={
                healthDetailsData?.hero?.hero_signup_title ||
                "Join the waitlist!"
              }
              formSubtitle={
                healthDetailsData?.hero?.hero_signup_subtitle ||
                "Our team will be in touch with you to understand your specific requirements."
              }
              defaultPackageSlug="health-for-others-ind-49"
              defaultSelectedGoal={
                healthDetailsData?.hero?.hero_signup_primary_pricing
                  ?.package_slug
              }
              priceConstant={healthDetailsData?.hero?.hero_cta_title_constant}
              price={{
                ...(healthDetailsData?.hero?.hero_signup_primary_pricing || {}),
              }}
              urlsArguments={urlsArguments}
              needMoreInfo={
                showCallBackCTA && "Need more information? Request a call back"
              }
              isFreeTrialPage={
                healthDetailsData?.hero?.free_trial_request || false
              }
            />
          </CallBackFormBox>
        </Col>
      </Group>
    </Wrapper>
  );
}

export default HowItWorks;

const Wrapper = styled(FlexDiv)`
  background-color: ${theme.colors.lightBlue};
  padding: 54px 0;
  @media only screen and (max-width: 1169px) {
    background-color: ${theme.colors.white};
    padding: 0;
  }
`;
const Col = styled(FlexDiv)`
  max-width: 50%;
  padding: 0 15px;
  width: 100%;
  justify-content: flex-end;
`;
const LCol = styled(FlexDiv)`
  max-width: 50%;
  padding: 0 15px;
  width: 100%;
  justify-content: flex-start;
  order: ${({ reverseOrderForMobile }) =>
    reverseOrderForMobile && -100} !important;
`;
const UList = styled.ul`
  padding-left: 0;
  margin: 0;
`;
const Icon = styled(SVGSprite)`
  height: 37px;
  width: 37px;
`;
const ListItem = styled.li`
  list-style: none;
  margin-bottom: 33px;
  &:last-child {
    margin-bottom: 0;
  }
  padding-left: 57px;
  position: relative;
  ${Icon} {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }
  @media only screen and (max-width: 1169px) {
    padding-left: 37px;
    ${Icon} {
      height: 25px;
      width: 25px;
    }
  }
`;
const CallBackFormBox = styled(FlexDiv)`
  background-color: ${theme.colors.white};
  padding: 35px;
  border: 1px solid ${theme.colors.border1};
  border-radius: 10px;
  max-width: 502px;
`;
const Group = styled(FlexContainer)`
  @media only screen and (max-width: 1169px) {
    flex-direction: column;
    padding: 0;
    ${LCol} {
      order: 1;
      background-color: ${theme.colors.lightBlue};
      padding: 52px 15px;
    }
    ${Col} {
      margin-bottom: 35px;
      order: 0;
      ${CallBackFormBox} {
        border: 0;
        padding: 35px 20px;
      }
    }
    ${Col},${LCol} {
      max-width: 100%;
      justify-content: center;
    }
  }
`;
